import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, MessageCircle, Clock, Instagram, Facebook } from 'lucide-react';
import { toast } from 'sonner';
const WHATSAPP_URL = 'https://wa.me/19084764884';
const SMS_URL = 'sms:+19084764884';
export function Contact() {
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    toast.success("Request received! We'll WhatsApp you back shortly.");
    (e.target as HTMLFormElement).reset();
  };
  return <section id="contact" className="py-24 md:py-32 bg-brand-light" data-id="element-53">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-54">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-55">
          <span className="text-brand-greenDark font-bold tracking-widest uppercase text-sm mb-4 block" data-id="element-56">
            ◆ Get in Touch
          </span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-brand-ink" data-id="element-57">
            Let's get your space{' '}
            <span className="italic font-serif text-brand-navy" data-id="element-58">
              sparkling.
            </span>
          </h2>
          <p className="text-lg text-slate-700" data-id="element-59">
            Text or WhatsApp us — it's the fastest way to book.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8" data-id="element-60">
          {/* Contact Form */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="lg:col-span-3" data-id="element-61">
            <form onSubmit={handleSubmit} className="bg-white p-8 md:p-10 rounded-3xl shadow-soft border border-slate-100 space-y-6" data-id="element-62">
              <h3 className="text-2xl font-bold text-brand-ink mb-2" data-id="element-63">
                Request a free quote
              </h3>
              <p className="text-sm text-slate-600 mb-6" data-id="element-64">
                Fill out the form or message us directly on WhatsApp.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-65">
                <div data-id="element-66">
                  <label htmlFor="name" className="block text-sm font-semibold text-slate-800 mb-2" data-id="element-67">
                    Full Name
                  </label>
                  <input type="text" id="name" required className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:ring-2 focus:ring-brand-navy/20 focus:border-brand-navy outline-none transition-all text-slate-900" placeholder="John Doe" data-id="element-68" />
                </div>
                <div data-id="element-69">
                  <label htmlFor="phone" className="block text-sm font-semibold text-slate-800 mb-2" data-id="element-70">
                    Phone Number
                  </label>
                  <input type="tel" id="phone" required className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:ring-2 focus:ring-brand-navy/20 focus:border-brand-navy outline-none transition-all text-slate-900" placeholder="(908) 000-0000" data-id="element-71" />
                </div>
              </div>

              <div data-id="element-72">
                <label htmlFor="service" className="block text-sm font-semibold text-slate-800 mb-2" data-id="element-73">
                  Service Needed
                </label>
                <select id="service" required className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:ring-2 focus:ring-brand-navy/20 focus:border-brand-navy outline-none transition-all bg-white text-slate-900" defaultValue="" data-id="element-74">
                  <option value="" disabled data-id="element-75">
                    Select a service...
                  </option>
                  <option value="house" data-id="element-76">House Cleaning</option>
                  <option value="office" data-id="element-77">Office & Commercial</option>
                  <option value="deep" data-id="element-78">Deep Cleaning</option>
                  <option value="move" data-id="element-79">Move-In / Move-Out</option>
                  <option value="carpet" data-id="element-80">Carpet & Upholstery</option>
                  <option value="window" data-id="element-81">Window Cleaning</option>
                  <option value="other" data-id="element-82">Other</option>
                </select>
              </div>

              <div data-id="element-83">
                <label htmlFor="message" className="block text-sm font-semibold text-slate-800 mb-2" data-id="element-84">
                  Tell us about your space
                </label>
                <textarea id="message" rows={4} required className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:ring-2 focus:ring-brand-navy/20 focus:border-brand-navy outline-none transition-all resize-none text-slate-900" placeholder="Square footage, special requests, preferred date..." data-id="element-85" />
              </div>

              <button type="submit" className="w-full py-4 rounded-xl bg-brand-navy text-white font-bold text-lg hover:bg-brand-navyDark transition-all hover:shadow-glow hover:-translate-y-0.5" data-id="element-86">
                Send Request
              </button>
            </form>
          </motion.div>

          {/* Contact Info */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="lg:col-span-2 space-y-6" data-id="element-87">
            {/* Quick contact card */}
            <div className="bg-brand-navy text-white rounded-3xl p-8 shadow-xl relative overflow-hidden" data-id="element-88">
              <motion.div animate={{
              scale: [1, 1.2, 1]
            }} transition={{
              duration: 4,
              repeat: Infinity
            }} className="absolute top-0 right-0 w-64 h-64 bg-brand-green/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-89" />

              <h3 className="text-xl font-bold mb-2 relative z-10" data-id="element-90">
                Fastest way to reach us
              </h3>
              <p className="text-white/80 text-sm mb-6 relative z-10" data-id="element-91">
                WhatsApp or text only — no phone calls.
              </p>

              <div className="space-y-3 relative z-10" data-id="element-92">
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 p-4 rounded-2xl bg-brand-green hover:bg-brand-greenDark transition-all hover:-translate-y-0.5 shadow-lg" data-id="element-93">
                  <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0" data-id="element-94">
                    <MessageCircle className="w-6 h-6 text-white" data-id="element-95" />
                  </div>
                  <div data-id="element-96">
                    <p className="text-xs uppercase tracking-wide text-white/80 font-semibold" data-id="element-97">
                      WhatsApp
                    </p>
                    <p className="text-lg font-bold text-white" data-id="element-98">
                      (908) 476-4884
                    </p>
                  </div>
                </a>

                <a href={SMS_URL} className="flex items-center gap-4 p-4 rounded-2xl bg-white/10 hover:bg-white/15 transition-all hover:-translate-y-0.5" data-id="element-99">
                  <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0" data-id="element-100">
                    <MessageCircle className="w-6 h-6 text-brand-gold" data-id="element-101" />
                  </div>
                  <div data-id="element-102">
                    <p className="text-xs uppercase tracking-wide text-white/70 font-semibold" data-id="element-103">
                      Text Message
                    </p>
                    <p className="text-lg font-bold text-white" data-id="element-104">
                      (908) 476-4884
                    </p>
                  </div>
                </a>
              </div>
            </div>

            {/* Location & Hours */}
            <div className="bg-white rounded-3xl p-8 shadow-soft border border-slate-100 space-y-5" data-id="element-105">
              <div className="flex items-start gap-4" data-id="element-106">
                <div className="w-11 h-11 rounded-full bg-brand-navy/5 flex items-center justify-center flex-shrink-0" data-id="element-107">
                  <MapPin className="w-5 h-5 text-brand-navy" data-id="element-108" />
                </div>
                <div data-id="element-109">
                  <h4 className="font-bold text-brand-ink mb-1" data-id="element-110">
                    Service Area
                  </h4>
                  <p className="text-slate-700 text-sm" data-id="element-111">
                    Clifton, New Jersey
                    <br data-id="element-112" />& Surrounding Areas
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4" data-id="element-113">
                <div className="w-11 h-11 rounded-full bg-brand-navy/5 flex items-center justify-center flex-shrink-0" data-id="element-114">
                  <Clock className="w-5 h-5 text-brand-navy" data-id="element-115" />
                </div>
                <div data-id="element-116">
                  <h4 className="font-bold text-brand-ink mb-1" data-id="element-117">
                    Working Hours
                  </h4>
                  <p className="text-slate-700 text-sm" data-id="element-118">
                    Mon – Sat: 8:00 AM – 7:00 PM
                    <br data-id="element-119" />
                    Sun: By appointment
                  </p>
                </div>
              </div>

              <div className="pt-4 border-t border-slate-100" data-id="element-120">
                <h4 className="font-bold text-brand-ink mb-3 text-sm" data-id="element-121">
                  Follow Us
                </h4>
                <div className="flex gap-3" data-id="element-122">
                  <a href="#" aria-label="Instagram" className="w-10 h-10 rounded-full bg-brand-navy text-white flex items-center justify-center hover:bg-brand-green transition-colors" data-id="element-123">
                    <Instagram className="w-5 h-5" data-id="element-124" />
                  </a>
                  <a href="#" aria-label="Facebook" className="w-10 h-10 rounded-full bg-brand-navy text-white flex items-center justify-center hover:bg-brand-green transition-colors" data-id="element-125">
                    <Facebook className="w-5 h-5" data-id="element-126" />
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}